import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { Expense } from '@otw/models'

dayjs.extend(utc)
dayjs.extend(timezone)

const isReviewedExpense = (expense: Pick<Expense, 'reviewStatus'>): boolean =>
  !expense.reviewStatus || expense.reviewStatus === 'reviewed'

/** Expense paid/week math uses America/New_York (same as `paidAt` display). */
export const EXPENSE_TZ = 'America/New_York'

export type ExpenseDateField = 'date' | 'paidAt'

export type PaidWeekBounds = {
  weekOffset: number
  start: Dayjs
  end: Dayjs
  label: string
}

/** Current instant expressed in New York local wall time (for Firestore `paidAt`). */
export const expenseNow = (): Date =>
  new Date(new Date().toLocaleString('en-US', { timeZone: EXPENSE_TZ }))

export const nyDayjs = (value?: Date | string | number | Dayjs | null): Dayjs => {
  if (value == null) return dayjs().tz(EXPENSE_TZ)
  return dayjs(value).tz(EXPENSE_TZ)
}

/** Sunday–Saturday week bounds in New York for `weekOffset` (0 = this week, -1 = last week). */
export const getPaidWeekBounds = (weekOffset = 0, now: Date = new Date()): PaidWeekBounds => {
  const start = nyDayjs(now).startOf('week').add(weekOffset, 'week')
  const end = start.endOf('week')
  return {
    weekOffset,
    start,
    end,
    label: `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`,
  }
}

export const isPaidInWeek = (
  expense: Pick<Expense, 'paid' | 'paidAt'>,
  bounds: Pick<PaidWeekBounds, 'start' | 'end'>,
): boolean => {
  if (expense.paid !== true || !(expense.paidAt instanceof Date)) return false
  const paidAt = nyDayjs(expense.paidAt)
  return !paidAt.isBefore(bounds.start, 'day') && !paidAt.isAfter(bounds.end, 'day')
}

/** True when marked paid during the current New York calendar week. */
export const isPaidThisWeek = (expense: Pick<Expense, 'paid' | 'paidAt'>, now: Date = new Date()): boolean =>
  isPaidInWeek(expense, getPaidWeekBounds(0, now))

export const getPaidExpensesForWeek = (
  expenses: Expense[],
  weekOffset = 0,
  now: Date = new Date(),
): Expense[] => {
  const bounds = getPaidWeekBounds(weekOffset, now)
  return expenses
    .filter(expense => isReviewedExpense(expense) && isPaidInWeek(expense, bounds))
    .sort((a, b) => {
      const aPaid = a.paidAt instanceof Date ? a.paidAt.getTime() : 0
      const bPaid = b.paidAt instanceof Date ? b.paidAt.getTime() : 0
      if (bPaid !== aPaid) return bPaid - aPaid
      if (b.amount !== a.amount) return b.amount - a.amount
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })
}

export const getPaidThisWeekExpenses = (expenses: Expense[], now: Date = new Date()): Expense[] =>
  getPaidExpensesForWeek(expenses, 0, now)

/**
 * Resolve the Date to store when confirming Mark paid.
 * Same calendar day as "now" in NY → click time; otherwise noon that day in NY.
 */
export const resolvePaidAtFromPicker = (paidDate: Dayjs, now: Date = expenseNow()): Date => {
  const nyNow = nyDayjs(now)
  if (paidDate.tz(EXPENSE_TZ).isSame(nyNow, 'day')) {
    return now
  }
  return paidDate.tz(EXPENSE_TZ).hour(12).minute(0).second(0).millisecond(0).toDate()
}

/** Inclusive calendar-day match for summary date filters (local midnight normalization). */
export const isDateInInclusiveRange = (value: Date, start: Date, end: Date): boolean => {
  const normalizedValue = new Date(value.getFullYear(), value.getMonth(), value.getDate())
  const normalizedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const normalizedEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  normalizedEnd.setDate(normalizedEnd.getDate() + 1)
  return normalizedValue >= normalizedStart && normalizedValue < normalizedEnd
}
